import {
  ApolloClient,
  from,
  HttpLink,
  InMemoryCache,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';

const httpLink = new HttpLink({
  uri: import.meta.env.VITE_API_URL,
});

const authLink = setContext((_, { headers }) => {
  const token = localStorage.getItem('redirector_token');
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
    },
  };
});

/**
 * Session expiry handler.
 *
 * Only force-logout on UNAUTHENTICATED errors that represent a REVOKED
 * session — i.e. we sent a token with the request and the server rejected it.
 * A fresh login attempt with wrong credentials also yields UNAUTHENTICATED,
 * and handling that here would:
 *   - wipe any auth state the user may already have in another tab,
 *   - force-navigate away before the Login component can render the inline
 *     error from its own `onError` callback.
 *
 * So: only react when the request carried an Authorization header.
 */
const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  if (graphQLErrors) {
    for (const err of graphQLErrors) {
      if (err.extensions?.code === 'UNAUTHENTICATED') {
        // authLink (later in the chain) always sets `authorization`: '' when
        // no token is stored, otherwise `Bearer <token>`.
        const { headers } = operation.getContext() as { headers: { authorization: string } };
        if (headers.authorization !== '') {
          localStorage.removeItem('redirector_token');
          window.location.href = '/login';
          return;
        }
        // No token was sent (e.g. the login mutation itself). Let the caller's
        // onError handler surface the message inline.
        return;
      }
      // Sanitised + user-facing errors are surfaced by individual callers.
      // We intentionally don't console.error them here to avoid noise when
      // errors are expected (bad input, conflicts, etc).
    }
  }
  if (networkError) {
    console.error('[Network error]:', networkError);
  }
});

export const apolloClient = new ApolloClient({
  link: from([errorLink, authLink, httpLink]),
  cache: new InMemoryCache(),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
    },
  },
});
